// Single source of truth for /tools/first-responder-home-buying.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/first-responder-home-buying";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "First Responder Home Buying Programs: Federal, National, and State Help";
export const PAGE_DESCRIPTION =
  "Which programs actually help police, firefighters, EMTs, and paramedics buy a home: Good Neighbor Next Door, FHA and USDA basics, Homes for Heroes, state and local examples, and how lenders count overtime and detail pay.";

export const LAST_REVIEWED = "August 12, 2026";

export const HERO_PROMISE =
  "Shift work, overtime, and detail pay make first responder income look complicated to an underwriter — and half the \"hero programs\" you find online are discounts, referrals, or bills that never passed. Here is what is actually active, who qualifies, and what to document before you apply.";

export const SECTIONS = [
  { id: "federal-programs", title: "Federal programs" },
  { id: "national-programs", title: "National programs compared" },
  { id: "state-local", title: "State and local examples" },
  { id: "income-documentation", title: "How lenders count your pay" },
  { id: "questions", title: "Questions before you apply" },
];

export const TRUST_BLOCK = {
  heading: "How this page is maintained",
  reviewed: LAST_REVIEWED,
  verification:
    "Every program below was checked against the administering agency's own published page at the last review date. Program terms, funding, and eligibility change without notice — treat this as a starting list and confirm details directly with the agency or a participating lender before you rely on them.",
  stories: [
    {
      id: "down-payment-planning",
      role: "Firefighter, 6 years on the job",
      text: "He assumed he needed 20% down and put off buying for three years. A county program covering part of the down payment, stacked with a state agency loan, closed the gap — but the county fund was first-come and reopened only once that year. Checking availability early is what made the timing work.",
    },
    {
      id: "variable-income",
      role: "Paramedic, dual-agency shifts",
      text: "Roughly a third of her income came from overtime and a second per-diem agency. Her first application stalled because she had no two-year history at the second job. Pulling year-to-date pay stubs and two years of W-2s up front, and asking the lender how they averaged the overtime, changed the qualifying number materially.",
    },
  ],
};

export type FederalProgram = {
  id: string;
  name: string;
  status: "active" | "proposed";
  what: string;
  who: string;
  catch: string;
};

export const FEDERAL_PROGRAMS: FederalProgram[] = [
  {
    id: "gnnd",
    name: "Good Neighbor Next Door (HUD)",
    status: "active",
    what: "A 50% discount off the list price of eligible HUD-owned homes in designated revitalization areas, structured as a silent second mortgage that is forgiven if you meet the occupancy term.",
    who: "Law enforcement officers, firefighters, emergency medical technicians, and pre-K through 12 teachers, buying as owner-occupants.",
    catch: "Inventory is the whole story. Only specific HUD-owned homes in specific revitalization areas are eligible, listings appear for a short window, and demand is settled by lottery when multiple qualified bidders apply. You must live in the home for the full required occupancy period or repay a prorated share of the discount.",
  },
  {
    id: "fha",
    name: "FHA loans",
    status: "active",
    what: "A government-insured mortgage allowing a low minimum down payment and more flexible credit standards than conventional financing.",
    who: "Any qualified buyer — there is no first responder eligibility category. It is listed here because most first responder assistance programs are layered on top of an FHA loan.",
    catch: "FHA requires mortgage insurance premiums that, on most current loan terms, remain for the life of the loan. It is not a first responder benefit, and no lender can offer you a special FHA rate for being one.",
  },
  {
    id: "usda",
    name: "USDA rural development loans",
    status: "active",
    what: "A zero-down-payment mortgage for homes in eligible rural and many suburban-fringe areas, with household income limits by county.",
    who: "Any qualified buyer within the income limits, buying in an eligible area — again, not first responder specific.",
    catch: "Both the property address and your household income must qualify. Many responders serve in a city but could buy in an eligible area just outside it, which is why this is worth checking on the USDA eligibility map rather than assuming.",
  },
  {
    id: "va",
    name: "VA loans (overlap)",
    status: "active",
    what: "Zero down payment, no monthly mortgage insurance, and competitive terms for eligible service members, veterans, and some surviving spouses.",
    who: "A large share of first responders are also veterans or National Guard members. If you have any service history, check VA eligibility first — it usually beats every other loan option available to you.",
    catch: "VA eligibility comes from military service, not from your current first responder role. You will need a Certificate of Eligibility, and the VA funding fee applies unless you are exempt.",
  },
];

export const HELPER_ACT = {
  heading: "HELPER Act status: proposed, not available",
  body:
    "The Homes for Every Local Protector, Educator, and Responder (HELPER) Act has been introduced in Congress in multiple sessions as a proposal to create a zero-down, no-mortgage-insurance FHA loan for first responders and teachers. It has not been enacted. There is no HELPER Act loan to apply for, no lender can originate one, and any site or loan officer marketing \"HELPER Act approval\" is describing a bill, not a program. Build your plan on active programs only, and revisit this if it ever passes.",
};

export type NationalRow = {
  id: string;
  name: string;
  programType: string;
  benefit: string;
  qualifies: string;
  offerType: "Discount" | "Grant" | "Referral" | "Referral / rebate";
  note: string;
};

export const NATIONAL_ROWS: NationalRow[] = [
  {
    id: "gnnd-row",
    name: "Good Neighbor Next Door",
    programType: "Federal, HUD-administered",
    benefit: "50% off the list price of an eligible HUD home",
    qualifies: "Law enforcement, firefighters, EMTs, pre-K–12 teachers",
    offerType: "Discount",
    note: "Largest dollar benefit on this list, and the hardest to use — it depends entirely on eligible inventory near you.",
  },
  {
    id: "homes-for-heroes",
    name: "Homes for Heroes",
    programType: "Private affiliate network",
    benefit: "A rebate at closing based on the affiliate agent's commission, plus reduced fees from participating lenders and title partners",
    qualifies: "Firefighters, EMS, law enforcement, military, healthcare workers, teachers",
    offerType: "Referral / rebate",
    note: "This is a referral network, not a grant. You must use their affiliated agent and partners to receive anything, the amount scales with your purchase price and the agent's commission, and rebates are restricted or prohibited in some states.",
  },
  {
    id: "hero-style",
    name: "\"Hero\" / Next Door style agent programs",
    programType: "Private brokerage marketing programs",
    benefit: "Commission rebates or credited fees, structured much like the above",
    qualifies: "Varies by brokerage; usually the same broad hero definition",
    offerType: "Referral",
    note: "Compare the rebate against what a strong local agent could negotiate for you outright. The program only pays if you route the transaction through their partner.",
  },
  {
    id: "closing-cost-funds",
    name: "Closing cost assistance funds",
    programType: "State agency, county, city, or employer-sponsored",
    benefit: "Grant or forgivable second covering some or all closing costs, commonly a few thousand dollars",
    qualifies: "Set locally — often income-capped, sometimes restricted to public service employees",
    offerType: "Grant",
    note: "Usually the most practical real money for responders, because it stacks with an FHA or state agency first mortgage. Availability is a ZIP code question and funds run out mid-year.",
  },
];

export type StateExample = {
  id: string;
  place: string;
  program: string;
  qualifies: string;
  helpType: string;
  repaymentTrigger: string;
  changesOften: string;
};

export const STATE_EXAMPLES: StateExample[] = [
  {
    id: "florida",
    place: "Florida",
    program: "Florida Housing \"Hometown Heroes\" style assistance through the state housing agency",
    qualifies: "Full-time employed Floridians in eligible occupations, including law enforcement, firefighters, and EMS, within county income limits; first-time buyer rules often apply, with exemptions for some responders and veterans.",
    helpType: "Down payment and closing cost assistance as a second mortgage layered on a state agency first mortgage, plus a reduced first mortgage rate in some cycles.",
    repaymentTrigger: "Typically due on sale, refinance, transfer of title, or when the home stops being your primary residence — often deferred with no monthly payment until then.",
    changesOften: "Funding rounds and the eligible occupation list. The program has been paused and restructured between legislative cycles, so confirm the current cycle before planning around it.",
  },
  {
    id: "texas",
    place: "Texas",
    program: "TSAHC Homes for Texas Heroes",
    qualifies: "Police and correctional officers, fire fighters and EMS personnel, allied health and nursing faculty, teachers and school staff, plus veterans, subject to income and purchase price limits by county.",
    helpType: "A grant or deferred forgivable second for down payment and closing costs, expressed as a percentage of the loan amount, paired with a fixed-rate first mortgage; a mortgage credit certificate may be available.",
    repaymentTrigger: "Grant options carry no repayment. Deferred forgivable seconds are forgiven after the stated occupancy term and become repayable if you sell or refinance before it.",
    changesOften: "The assistance percentage and interest rate options reprice frequently, and program funds are reserved by participating lenders as loans lock.",
  },
  {
    id: "georgia",
    place: "Georgia",
    program: "Georgia Dream with the PEN (Protectors, Educators, Nurses) assistance tier",
    qualifies: "Active-duty military, first responders including law enforcement and firefighters, educators, and health care providers who meet Georgia Dream income, asset, and purchase price limits.",
    helpType: "A larger down payment assistance second mortgage than the standard tier, paired with the Georgia Dream first mortgage.",
    repaymentTrigger: "Zero-interest deferred second, repaid when you sell, refinance, or no longer occupy the home as your primary residence.",
    changesOften: "Assistance amounts and county income and purchase price caps are updated periodically; you must also complete an approved homebuyer education course.",
  },
  {
    id: "tennessee",
    place: "Tennessee",
    program: "THDA Great Choice with Homeownership for the Brave",
    qualifies: "Active-duty service members, veterans, reservists, and their spouses receive an interest rate reduction; broader THDA down payment assistance is available to eligible buyers including many public service workers within income limits.",
    helpType: "Reduced first mortgage rate for eligible military households, plus a down payment assistance second on the standard Great Choice program.",
    repaymentTrigger: "Deferred assistance options are forgiven over a set occupancy term; amortizing options are repaid monthly alongside the first mortgage.",
    changesOften: "Which assistance structure is offered, and the income and acquisition cost limits by county.",
  },
  {
    id: "san-francisco",
    place: "San Francisco, CA",
    program: "City down payment assistance loan programs, including the Teacher Next Door style and general DALP city loans",
    qualifies: "First-time buyers who live or work in San Francisco within city income limits; some tiers give priority to city employees, educators, and public service workers buying within city limits.",
    helpType: "Large deferred-payment or shared-appreciation second loans sized to a very high-cost market.",
    repaymentTrigger: "Repaid on sale, refinance, or when the home stops being your primary residence. Shared appreciation structures also return a share of the home's appreciation to the city, not just the original amount.",
    changesOften: "Funding rounds, lottery timing, maximum loan amounts, and whether the current cycle is even open for applications.",
  },
];

export const STATE_DISCLAIMER =
  "These five are illustrations of how state and local help is structured, not a complete list and not current program terms. Every state housing finance agency and many counties and cities run their own version. Confirm current amounts, income limits, and open funding rounds with the administering agency before you apply.";

export const INCOME_GUIDANCE = {
  heading: "How lenders count overtime, shift differential, and detail pay",
  intro:
    "This is where first responder files stall. Base pay is easy. The other half of your check — overtime, holdover, shift differential, detail or off-duty work, stipends, and a second per-diem job — is treated as variable income, and underwriters apply their own rules to it.",
  rules: [
    {
      id: "history",
      label: "Two-year history is the default expectation",
      text: "Most underwriting guidelines want a two-year history of receiving the variable income before it can be counted, and evidence that it is likely to continue. Some allow a shorter history with strong documentation — ask your loan officer which guideline they are applying to your file before you assume the income is dead.",
    },
    {
      id: "averaging",
      label: "Variable income is averaged, not annualized from your best month",
      text: "Lenders typically average overtime and differential over 12 to 24 months. A record year followed by a slow year averages down. If your overtime is trending up, ask whether they will use the shorter average and what documentation supports it.",
    },
    {
      id: "declining",
      label: "Declining income gets haircut or excluded",
      text: "If this year's overtime is running below last year's, many underwriters use the lower figure or drop it entirely. Know your year-to-date number before you shop, not after you are under contract.",
    },
    {
      id: "detail",
      label: "Detail and off-duty work is scrutinized separately",
      text: "Paid detail routed through your department and shown on your pay stub is easier to document than side work paid by a third party. If a private employer pays you directly, expect it to be treated like a second job with its own history requirement, and possibly self-employment documentation.",
    },
    {
      id: "second-job",
      label: "A second agency or per-diem job needs its own paper trail",
      text: "Two years at the second employer, or a documented pattern of continuous employment in the same line of work, is the usual bar. Gaps between contracts need a written explanation.",
    },
    {
      id: "pension",
      label: "Pension contributions and union dues are not deductions from qualifying income",
      text: "They reduce your take-home pay but not your gross qualifying income, which can make the approved payment feel higher than your budget really supports. Set your own ceiling from take-home, not from the pre-approval maximum.",
    },
  ],
  checklist: {
    heading: "Document checklist to bring to your first lender conversation",
    items: [
      "Most recent 30 days of pay stubs from every employer, showing year-to-date totals broken out by base, overtime, and differential",
      "W-2s for the last two years from every employer, including a second agency or per-diem role",
      "A written statement or verification of employment showing your hire date, position, and whether overtime is expected to continue",
      "Two years of federal tax returns if you have any self-employed detail work, 1099 income, or side business",
      "Documentation of any stipends: uniform, education incentive, hazard, or longevity pay, and whether each is recurring",
      "Two months of statements for every account holding down payment funds, with any large deposit explained and sourced",
      "Gift letter and donor statements if family is contributing, prepared before the funds move",
      "Proof of your first responder status: department ID, employment letter, or certification, which most hero programs require at application",
      "Your union or department benefits summary, in case an employer-sponsored housing benefit exists that you have not been told about",
    ],
  },
};

export const CTA_PRIMARY = {
  label: "Talk to a lender who understands first responder income",
  href: "/lender-match",
};
export const CTA_SECONDARY = { label: "Check programs by state", href: "/stack" };

export const FAQS = [
  {
    id: "gnnd-how",
    q: "How does Good Neighbor Next Door actually work?",
    a: "HUD lists eligible homes it owns in designated revitalization areas. Qualified law enforcement officers, firefighters, EMTs, and pre-K through 12 teachers can bid to buy one at 50% off the list price. The discount is recorded as a silent second mortgage that is forgiven if you occupy the home for the full required period. If multiple qualified buyers bid on the same home, the winner is selected by lottery. The limitation is inventory: eligible homes appear irregularly and only in specific areas, so many responders never see one near where they work.",
  },
  {
    id: "who-counts",
    q: "Who counts as a first responder for these programs?",
    a: "It depends on the program, and the differences matter. Good Neighbor Next Door names law enforcement officers, firefighters, and emergency medical technicians. State programs often use a broader public service list that can include correctional officers, dispatchers, nurses, and teachers. Private networks like Homes for Heroes use the widest definition of all. Never assume your role qualifies because a similar program accepted it — check the specific eligibility language before you build a plan around it.",
  },
  {
    id: "state-help",
    q: "Is there first responder help in my state?",
    a: "Most states run down payment assistance through a state housing finance agency, and a meaningful number add a public service or hero tier with a larger assistance amount or a reduced rate. Counties, cities, and even individual departments add their own. The practical route is to start with your state housing finance agency's official list of participating lenders and programs, then check county and city programs for the specific area you want to buy in.",
  },
  {
    id: "helper-act",
    q: "Can I get a HELPER Act loan?",
    a: "No. The HELPER Act is proposed federal legislation that has been introduced in Congress but has not been enacted. There is no loan product to apply for and no lender can originate one. If a site or a loan officer is marketing HELPER Act approval, they are describing a bill, not a program you can use today.",
  },
  {
    id: "overtime-counts",
    q: "Will my overtime count toward qualifying for a mortgage?",
    a: "Often yes, but on the lender's terms. The usual expectation is a two-year history of receiving it plus evidence it will continue, and the amount used is an average over 12 to 24 months rather than your best recent months. If your overtime is declining year over year, expect it to be reduced or excluded. Ask your loan officer directly which guideline they are applying and what averaging period they will use — the answer can change your maximum by tens of thousands of dollars.",
  },
  {
    id: "stack",
    q: "Can I combine a hero program with other down payment assistance?",
    a: "Sometimes. Federal, state, county, city, and employer help can often be layered, but each program sets its own rules about what it will sit behind, and some prohibit combining with other subsidies. Good Neighbor Next Door's discount can generally be paired with FHA financing, while commission rebate programs are unrelated to your loan and are a closing credit rather than assistance. Confirm stacking with each administrator, and get the approved combination in writing before you go under contract.",
  },
  {
    id: "credit",
    q: "Do first responder programs have lower credit requirements?",
    a: "Generally no. Most assistance programs require you to qualify for an underlying first mortgage on standard terms, so the loan program's credit minimum still applies, and many assistance programs set their own minimum score that is higher than FHA's floor. The benefit is money toward down payment and closing costs or a rate reduction, not a relaxed credit standard.",
  },
];

export const DISCLAIMER =
  "This guide is educational and is not lending, legal, or tax advice. Program terms, funding availability, income limits, and eligibility are set by the administering agency and change frequently — several of the programs described here have been paused, restructured, or repriced between funding cycles. Verify every detail directly with the agency or a participating lender before you apply. Home Approach is not affiliated with HUD, any state housing finance agency, or the private networks named on this page.";

export const FIRST_RESPONDER_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
